import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Container, Paper, Divider } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getColorLabel } from '../utils/colors';
import { registerMiniOrder, MiniOrderItem } from '../extraApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const OrderConfirmation = () => {
  const { clearCart } = useCart();
  const { setHasNewOrderNotification } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [summaryItems, setSummaryItems] = useState<any[]>([]);
  const [summaryTotal, setSummaryTotal] = useState<number>(0);
  const [summaryItemCount, setSummaryItemCount] = useState<number>(0);
  const [summaryShippingAmount, setSummaryShippingAmount] = useState<number | null>(null);
  const location = useLocation();
  const hasFinalizedRef = useRef(false);

  useEffect(() => {
    if (hasFinalizedRef.current) {
      return;
    }
    hasFinalizedRef.current = true;

    const finalizeOrder = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const sessionIdFromUrl = searchParams.get('session_id');
        const stored =
          sessionStorage.getItem('pending_order') ||
          localStorage.getItem('pending_order_backup');

        let pending: any = null;
        if (stored) {
          pending = JSON.parse(stored);
        } else if (sessionIdFromUrl) {
          try {
            const summaryRes = await fetch(
              `${API_BASE_URL}/checkout-sessions/${encodeURIComponent(sessionIdFromUrl)}/summary`
            );
            if (summaryRes.ok) {
              const summary = await summaryRes.json();
              pending = {
                items: Array.isArray(summary.items) ? summary.items : [],
                total: typeof summary.total === 'number' ? summary.total : 0,
                shippingAmount:
                  typeof summary.shippingAmount === 'number' ? summary.shippingAmount : null,
                date:
                  typeof summary.createdAt === 'string' && summary.createdAt
                    ? summary.createdAt
                    : new Date().toISOString(),
                authToken: null,
              };
            }
          } catch (e) {
            console.error('Erreur lors du fallback de récupération de session Stripe', e);
          }
        }

        if (!pending) {
          setProcessing(false);
          return;
        }

        const items = Array.isArray(pending.items) ? pending.items : [];
        const total = typeof pending.total === 'number' ? pending.total : 0;

        const rawShippingAmount =
          typeof pending.shippingAmount === 'number' &&
          Number.isFinite(pending.shippingAmount)
            ? pending.shippingAmount
            : null;

        const shippingPostalCode =
          typeof pending.shippingPostalCode === 'string'
            ? pending.shippingPostalCode.trim()
            : '';

        let shippingDistance: 'short' | 'medium' | 'long' | null = null;
        if (shippingPostalCode) {
          const zoneACodes = ['4500', '4520', '4530', '4540', '4570'];
          const zoneBCodes = [
            '4537',
            '4470',
            '4480',
            '4550',
            '4557',
            '4577',
            '4560',
            '4217',
            '4210',
            '4260',
            '5300',
            '5352',
            '5370',
          ];

          if (zoneACodes.includes(shippingPostalCode)) {
            shippingDistance = 'short';
          } else if (zoneBCodes.includes(shippingPostalCode)) {
            shippingDistance = 'medium';
          } else {
            shippingDistance = 'long';
          }
        }

        const shippingAmountForBackend =
          rawShippingAmount != null && shippingDistance === 'long' && rawShippingAmount === 0
            ? null
            : rawShippingAmount;

        const shippingAmountForSummary =
          rawShippingAmount != null
            ? rawShippingAmount > 0
              ? rawShippingAmount
              : rawShippingAmount === 0 && shippingDistance === 'short'
              ? 0
              : null
            : null;

        const authToken: string | null =
          typeof pending.authToken === 'string' && pending.authToken.trim() !== ''
            ? pending.authToken
            : null;

        const createdAtForMini =
          typeof pending.date === 'string' && pending.date.trim() !== ''
            ? pending.date
            : new Date().toISOString();

        const miniOrderItems: MiniOrderItem[] = items
          .map((item: any) => {
            const productId =
              typeof item.id === 'number' && Number.isFinite(item.id)
                ? item.id
                : Number(item.id);
            const quantity =
              typeof item.quantity === 'number'
                ? item.quantity
                : Number(item.quantity) || 0;

            if (!Number.isFinite(productId) || productId <= 0 || quantity <= 0) {
              return null;
            }

            const name =
              typeof item.name === 'string' && item.name.trim()
                ? item.name
                : String(item.name ?? 'Produit');

            const size =
              typeof item.size === 'string' && item.size.trim() !== ''
                ? item.size
                : undefined;

            const color =
              typeof item.color === 'string' && item.color.trim() !== ''
                ? item.color
                : undefined;

            return {
              productId,
              name,
              size,
              color,
              quantity,
            } as MiniOrderItem;
          })
          .filter((i: MiniOrderItem | null): i is MiniOrderItem => i !== null);

        if (items.length > 0 && total > 0) {
          let miniOrderId: number | null = null;
          let shouldRegisterMiniOrder = false;

          try {
            const itemCount = items.reduce(
              (sum: number, item: any) => sum + (typeof item.quantity === 'number' ? item.quantity : 0),
              0
            );
            setSummaryItems(items);
            setSummaryTotal(total);
            setSummaryItemCount(itemCount);
            setSummaryShippingAmount(shippingAmountForSummary);
          } catch (e) {
            console.error('Erreur lors de la préparation du récapitulatif de commande', e);
          }

          if (authToken) {
            try {
              const res = await fetch(`${API_BASE_URL}/orders`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                  items,
                  total,
                  sessionId: sessionIdFromUrl,
                  shippingAmount: shippingAmountForBackend,
                }),
              });
              if (!res.ok) {
                console.error("Erreur lors de l'enregistrement de la commande côté serveur");
              } else {
                const data = await res.json().catch(() => null);
                if (data && typeof data.id === 'number') {
                  setOrderId(data.id);
                  miniOrderId = data.id;
                  shouldRegisterMiniOrder = true;
                  try {
                    setHasNewOrderNotification(true);
                  } catch (e) {
                    console.error('Erreur lors de la mise à jour de la notification de commande', e);
                  }
                } else {
                  shouldRegisterMiniOrder = true;
                }
              }
            } catch (e) {
              console.error(
                "Erreur réseau lors de l'enregistrement de la commande côté serveur",
                e
              );
            }
          } else {
            try {
              const res = await fetch(`${API_BASE_URL}/guest-orders`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  items,
                  total,
                  sessionId: sessionIdFromUrl,
                  shippingAmount: shippingAmountForBackend,
                }),
              });

              if (!res.ok) {
                console.error("Erreur lors de l'enregistrement de la commande invitée côté serveur");
              } else {
                const data = await res.json().catch(() => null);
                if (data && typeof data.id === 'number') {
                  setOrderId(data.id);
                  miniOrderId = data.id;
                }
                shouldRegisterMiniOrder = true;
              }
            } catch (e) {
              console.error(
                "Erreur réseau lors de l'enregistrement de la commande invitée côté serveur",
                e
              );
            }
          }

          if (shouldRegisterMiniOrder && miniOrderItems.length > 0) {
            try {
              await registerMiniOrder({
                orderId: miniOrderId,
                createdAt: createdAtForMini,
                items: miniOrderItems,
              });
            } catch (e) {
              console.error(
                "Erreur lors de l'enregistrement de la commande dans le mini backend",
                e
              );
            }
          }
        }

        clearCart();
        sessionStorage.removeItem('pending_order');
        localStorage.removeItem('pending_order_backup');
      } catch (e) {
        console.error('Erreur lors de la finalisation de la commande après le paiement', e);
        setError('Une erreur est survenue lors de la finalisation de votre commande.');
      } finally {
        setProcessing(false);
      }
    };

    finalizeOrder();
  }, [clearCart, location.search, setHasNewOrderNotification]);

  const itemsTotal = summaryItems.reduce((sum, item) => {
    const quantity =
      typeof item.quantity === 'number' ? item.quantity : Number(item.quantity) || 0;
    const price = typeof item.price === 'number' ? item.price : Number(item.price) || 0;
    return sum + quantity * price;
  }, 0);

  const shippingForSummary =
    typeof summaryShippingAmount === 'number' &&
    Number.isFinite(summaryShippingAmount)
      ? summaryShippingAmount
      : 0;

  const rawPromoAmount = itemsTotal + shippingForSummary - summaryTotal;
  const hasPromo = rawPromoAmount > 0.01;
  const promoAmount = hasPromo ? rawPromoAmount : 0;

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {processing && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Finalisation de votre commande...
            </Typography>
          </Box>
        )}
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        <Box sx={{ mb: 4 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
        </Box>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Commande confirmée !
        </Typography>
        
        <Typography variant="body1" paragraph>
          Merci pour votre achat ! Votre commande a été enregistrée avec succès.
        </Typography>

        {orderId !== null && (
          <Typography variant="body2" color="text.secondary" paragraph>
            Numéro de commande&nbsp;: <strong>#{orderId}</strong>
          </Typography>
        )}

        {orderId !== null && (
          <Typography variant="body2" color="text.secondary" paragraph>
            Un email de confirmation de votre commande vous a été envoyé. Si vous ne le voyez
            pas dans votre boîte de réception, pensez à vérifier vos spams / courriers
            indésirables.
          </Typography>
        )}

        {summaryItems.length > 0 && (
          <Box sx={{ mt: 4, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>
              Récapitulatif de votre commande
            </Typography>
            <Box
              component="ul"
              sx={{ listStyle: 'none', p: 0, m: 0 }}
            >
              {summaryItems.map((item: any, index: number) => {
                const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                const price = typeof item.price === 'number' ? item.price : Number(item.price) || 0;
                const lineTotal = quantity * price;

                return (
                  <Box
                    key={`${item.id || index}-${index}`}
                    component="li"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 0.5,
                    }}
                  >
                    <Box mr={2}>
                      <Typography variant="body2">{item.name}</Typography>
                      {(item.size || item.color) && (
                        <Typography variant="caption" color="text.secondary">
                          {item.size && `Taille : ${item.size}`}
                          {item.size && item.color && ' • '}
                          {item.color && `Couleur : ${getColorLabel(item.color)}`}
                        </Typography>
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2">
                        x{quantity} — {lineTotal.toFixed(2)} €
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Nombre d'articles : <strong>{summaryItemCount}</strong>
            </Typography>
            {hasPromo && (
              <Typography variant="body2" color="text.secondary">
                Réduction : <strong>-{promoAmount.toFixed(2)} €</strong>
              </Typography>
            )}
            {summaryShippingAmount !== null && (
              <Typography variant="body2" color="text.secondary">
                Frais de livraison : <strong>{summaryShippingAmount.toFixed(2)} €</strong>
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              Total payé : <strong>{summaryTotal.toFixed(2)} €</strong>
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 4 }}>
          <Button 
            component={Link} 
            to="/products" 
            variant="contained" 
            color="primary" 
            size="large"
            sx={{ mr: 2 }}
          >
            Retour à la boutique
          </Button>
          
          <Button 
            component={Link} 
            to="/" 
            variant="outlined" 
            size="large"
          >
            Page d'accueil
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderConfirmation;
